/**
 * IndexedDB key storage for EC P-384 encryption keys.
 *
 * Each user's own key set holds two key pairs:
 *   - Encryption pair (ECDH P-384): used for ECDH key agreement → AES-256-GCM
 *   - Signing pair   (ECDSA P-384): used for SHA-384 message signatures
 *
 * Peer public keys (received via @keys) are stored by username so they can be
 * looked up when encrypting outbound messages or decrypting inbound ones.
 *
 * DB_VERSION 2: migrated from RSA-OAEP (v1) to EC P-384 (v2).
 * The upgrade handler clears all v1 data — users regenerate keys automatically.
 */

import { KeyPairInfo } from "@/utils/crypto";

const DB_NAME = "SpaceLinqCrypto";
const DB_VERSION = 3;
const PRIVATE_KEYS_STORE = "privateKeys";
const PUBLIC_KEYS_STORE = "publicKeys";
const SYMMETRIC_KEYS_STORE = "symmetricKeys";

interface StoredPrivateKey {
  keyId: string;
  userId: number;
  encryptPrivateKeyJwk: JsonWebKey;
  encryptPublicKeyJwk: JsonWebKey;
  signPrivateKeyJwk: JsonWebKey;
  signPublicKeyJwk: JsonWebKey;
  createdAt: number;
  isActive: boolean;
}

interface StoredPublicKey {
  keyId: string;
  userId: number;
  username: string;
  encryptPublicKeyJwk: JsonWebKey;
  signPublicKeyJwk: JsonWebKey;
  createdAt: number;
  receivedAt: number;
  /** Unix seconds from the sender's timestamp_generated field — identifies which key pair was used. */
  timestampGenerated?: number;
}

export interface ActiveKeySet {
  keyId: string;
  encryptPrivateKeyJwk: JsonWebKey;
  encryptPublicKeyJwk: JsonWebKey;
  signPrivateKeyJwk: JsonWebKey;
  signPublicKeyJwk: JsonWebKey;
  /** Unix ms timestamp of when this key pair was generated. Used as stable timestamp_generated in sendkeys. */
  createdAt: number;
}

export interface PeerPublicKeySet {
  encryptPublicKeyJwk: JsonWebKey;
  signPublicKeyJwk: JsonWebKey;
  /** Unix seconds from the sender's timestamp_generated. Used to match against r_e2e_keys_version. */
  timestampGenerated?: number;
}

interface StoredSymmetricKey {
  groupAddress: string;   // keyPath — unique per group
  groupName: string;
  ownerUsername: string;
  symmetricKeyB64: string;
}

export interface GroupSymmetricKey {
  groupAddress: string;
  groupName: string;
  ownerUsername: string;
  symmetricKeyB64: string;
}

// ---------------------------------------------------------------------------
// Database
// ---------------------------------------------------------------------------

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const oldVersion = event.oldVersion;

      // v1 → v2: RSA keys are incompatible with the new EC scheme.
      // Drop the old stores entirely; users will regenerate their key pairs.
      if (oldVersion < 2) {
        if (db.objectStoreNames.contains(PRIVATE_KEYS_STORE)) {
          db.deleteObjectStore(PRIVATE_KEYS_STORE);
        }
        if (db.objectStoreNames.contains(PUBLIC_KEYS_STORE)) {
          db.deleteObjectStore(PUBLIC_KEYS_STORE);
        }
      }

      if (!db.objectStoreNames.contains(PRIVATE_KEYS_STORE)) {
        const store = db.createObjectStore(PRIVATE_KEYS_STORE, { keyPath: "keyId" });
        store.createIndex("userId", "userId", { unique: false });
        store.createIndex("isActive", "isActive", { unique: false });
      }

      if (!db.objectStoreNames.contains(PUBLIC_KEYS_STORE)) {
        const store = db.createObjectStore(PUBLIC_KEYS_STORE, { keyPath: "keyId" });
        store.createIndex("userId", "userId", { unique: false });
        store.createIndex("username", "username", { unique: false });
      }

      // v2 → v3: add symmetric keys store for group encryption.
      if (!db.objectStoreNames.contains(SYMMETRIC_KEYS_STORE)) {
        db.createObjectStore(SYMMETRIC_KEYS_STORE, { keyPath: "groupAddress" });
      }
    };
  });
}

// ---------------------------------------------------------------------------
// Own key pair storage
// ---------------------------------------------------------------------------

export async function storeKeyPair(
  userId: number,
  keyPairInfo: KeyPairInfo,
  isActive: boolean = true
): Promise<void> {
  const db = await openDatabase();

  const [encryptPrivateKeyJwk, signPrivateKeyJwk] = await Promise.all([
    crypto.subtle.exportKey("jwk", keyPairInfo.encryptPrivateKey),
    crypto.subtle.exportKey("jwk", keyPairInfo.signPrivateKey),
  ]);

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PRIVATE_KEYS_STORE], "readwrite");

    transaction.oncomplete = () => { db.close(); resolve(); };
    transaction.onerror = () => { db.close(); reject(transaction.error); };
    transaction.onabort = () => { db.close(); reject(new DOMException("Transaction aborted")); };

    const store = transaction.objectStore(PRIVATE_KEYS_STORE);

    // Deactivate any existing active key for this user
    if (isActive) {
      const req = store.index("userId").openCursor(IDBKeyRange.only(userId));
      req.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const key = cursor.value as StoredPrivateKey;
          if (key.isActive) {
            key.isActive = false;
            cursor.update(key);
          }
          cursor.continue();
        }
      };
    }

    const record: StoredPrivateKey = {
      keyId: keyPairInfo.keyId,
      userId,
      encryptPrivateKeyJwk,
      encryptPublicKeyJwk: keyPairInfo.encryptPublicKeyJwk,
      signPrivateKeyJwk,
      signPublicKeyJwk: keyPairInfo.signPublicKeyJwk,
      createdAt: Date.now(),
      isActive,
    };

    store.put(record);
  });
}

/**
 * Returns the full active key set for a user (all four keys as JWKs).
 */
export async function getActiveKeySet(userId: number): Promise<ActiveKeySet | null> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PRIVATE_KEYS_STORE], "readonly");
    const store = transaction.objectStore(PRIVATE_KEYS_STORE);
    const request = store.index("userId").openCursor(IDBKeyRange.only(userId));

    let found: StoredPrivateKey | null = null;

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        const key = cursor.value as StoredPrivateKey;
        if (key.isActive) found = key;
        cursor.continue();
      } else {
        if (found) {
          resolve({
            keyId: found.keyId,
            encryptPrivateKeyJwk: found.encryptPrivateKeyJwk,
            encryptPublicKeyJwk: found.encryptPublicKeyJwk,
            signPrivateKeyJwk: found.signPrivateKeyJwk,
            signPublicKeyJwk: found.signPublicKeyJwk,
            createdAt: found.createdAt,
          });
        } else {
          resolve(null);
        }
        db.close();
      }
    };

    request.onerror = () => { reject(request.error); db.close(); };
  });
}

export async function hasPrivateKey(userId: number): Promise<boolean> {
  return (await getActiveKeySet(userId)) !== null;
}

export async function getActiveKeyId(userId: number): Promise<string | null> {
  const keySet = await getActiveKeySet(userId);
  return keySet?.keyId ?? null;
}

// ---------------------------------------------------------------------------
// Peer public key storage
// ---------------------------------------------------------------------------

export async function storePublicKey(
  keyId: string,
  userId: number,
  username: string,
  encryptPublicKeyJwk: JsonWebKey,
  signPublicKeyJwk: JsonWebKey,
  timestampGenerated?: number
): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PUBLIC_KEYS_STORE], "readwrite");

    transaction.oncomplete = () => db.close();
    transaction.onerror = () => { db.close(); reject(transaction.error); };

    const store = transaction.objectStore(PUBLIC_KEYS_STORE);

    // Delete any existing records for this username before writing the new one.
    // This prevents stale records (different keyIds from old code paths) from
    // piling up and causing IDBIndex.get() to return the wrong key.
    const usernameIndex = store.index("username");
    const cursorReq = usernameIndex.openCursor(IDBKeyRange.only(username));
    cursorReq.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        // All stale records removed — now write the fresh key.
        const record: StoredPublicKey = {
          keyId,
          userId,
          username,
          encryptPublicKeyJwk,
          signPublicKeyJwk,
          createdAt: Date.now(),
          receivedAt: Date.now(),
          ...(timestampGenerated !== undefined && { timestampGenerated }),
        };
        const putReq = store.put(record);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      }
    };
    cursorReq.onerror = () => reject(cursorReq.error);
  });
}

export async function getPublicKeySetByUsername(
  username: string
): Promise<PeerPublicKeySet | null> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PUBLIC_KEYS_STORE], "readonly");
    const store = transaction.objectStore(PUBLIC_KEYS_STORE);
    const request = store.index("username").get(username);

    request.onsuccess = () => {
      const stored = request.result as StoredPublicKey | undefined;
      if (stored) {
        // Silently skip legacy RSA keys — they are incompatible with ECDH.
        // The peer needs to re-login to regenerate and re-share their EC keys.
        if (stored.encryptPublicKeyJwk.kty !== "EC") {
          resolve(null);
        } else {
          resolve({
            encryptPublicKeyJwk: stored.encryptPublicKeyJwk,
            signPublicKeyJwk: stored.signPublicKeyJwk,
            timestampGenerated: stored.timestampGenerated,
          });
        }
      } else {
        resolve(null);
      }
      db.close();
    };

    request.onerror = () => { reject(request.error); db.close(); };
  });
}

// ---------------------------------------------------------------------------
// Symmetric keys (group encryption)
// ---------------------------------------------------------------------------

export async function storeSymmetricKey(
  groupAddress: string,
  groupName: string,
  ownerUsername: string,
  symmetricKeyB64: string
): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SYMMETRIC_KEYS_STORE], "readwrite");
    const store = transaction.objectStore(SYMMETRIC_KEYS_STORE);

    const record: StoredSymmetricKey = {
      groupAddress,
      groupName,
      ownerUsername,
      symmetricKeyB64,
    };

    const request = store.put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
  });
}

export async function getSymmetricKey(
  groupAddress: string
): Promise<GroupSymmetricKey | null> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([SYMMETRIC_KEYS_STORE], "readonly");
    const store = transaction.objectStore(SYMMETRIC_KEYS_STORE);
    const request = store.get(groupAddress);

    request.onsuccess = () => {
      const record = request.result as StoredSymmetricKey | undefined;
      if (record) {
        resolve({
          groupAddress: record.groupAddress,
          groupName: record.groupName,
          ownerUsername: record.ownerUsername,
          symmetricKeyB64: record.symmetricKeyB64,
        });
      } else {
        resolve(null);
      }
      db.close();
    };

    request.onerror = () => { reject(request.error); db.close(); };
  });
}

export async function hasSymmetricKey(groupAddress: string): Promise<boolean> {
  return (await getSymmetricKey(groupAddress)) !== null;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export async function getAllPublicKeys(): Promise<StoredPublicKey[]> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([PUBLIC_KEYS_STORE], "readonly");
    const store = transaction.objectStore(PUBLIC_KEYS_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result as StoredPublicKey[]);
      db.close();
    };
    request.onerror = () => { reject(request.error); db.close(); };
  });
}

// ---------------------------------------------------------------------------
// Cleanup
// ---------------------------------------------------------------------------

export async function clearAllKeys(): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      [PRIVATE_KEYS_STORE, PUBLIC_KEYS_STORE, SYMMETRIC_KEYS_STORE],
      "readwrite"
    );

    transaction.oncomplete = () => { resolve(); db.close(); };
    transaction.onerror = () => { reject(transaction.error); db.close(); };

    transaction.objectStore(PRIVATE_KEYS_STORE).clear();
    transaction.objectStore(PUBLIC_KEYS_STORE).clear();
    transaction.objectStore(SYMMETRIC_KEYS_STORE).clear();
  });
}
